import { Fragment, useState, useEffect } from "react";
import {
  setUserEntry,
  customerRegister,
  UpdateUser,
  buildProfileUpdatePayload,
} from "../../server/Customer";
import { useDispatch, useSelector } from "react-redux";
import { globalFileServer } from "../../../axiosInstance";
import { useForm } from "react-hook-form";
import Input from "../containers/InputForm";
import { validPhoneNumber } from "../../ValidFunction";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { setPreload } from "../../server/CartsOpen";
import showAlert from "../../server/ShowAlert";
import { ChevronLeft } from "lucide-react";

const UserEntry = ({
  parentName,
  onInlineError,
  onClearInlineError,
  onRegistered,
}) => {
  const isGuest = localStorage.getItem("userRole")?.includes("guest");

  const schema = yup
    .object({
      email:
        parentName === "signIn"
          ? yup
              .string()
              .email("כתובת מייל לא תקינה")
              .required("דואר אלקטרוני חובה")
          : yup.string().nullable(),
      firstName: yup.string().required("שם פרטי חובה"),
      lastName: yup.string().required("שם משפחה חובה"),
      id:
        parentName === "signIn"
          ? yup
              .string()
              .nullable()
              .matches(/^\d*$/, "תעודת זהות חייבת להכיל ספרות בלבד")
          : yup.string().nullable(),
      phone2:
        parentName === "signIn"
          ? yup
              .string()
              .nullable()
              .matches(validPhoneNumber, "מספר טלפון לא תקין")
          : yup.string().strip(),
      phone: yup
        .string()
        .required("חובה להכניס מספר טלפון")
        .matches(validPhoneNumber, "מספר טלפון לא תקין"),

      password:
        parentName === "signIn"
          ? yup
              .string()
              .required("חובה להכניס סיסמה")
              .min(8, "לפחות 8 תווים")
              .max(72, "מקסימום 72 תווים")
          : yup.string().nullable(),

      confirmPass:
        parentName === "signIn"
          ? yup
              .string()
              .required("חובה לאשר את הסיסמה")
              .oneOf([yup.ref("password"), null], "הסיסמות אינן תואמות")
          : yup.string().nullable(),

      Verifiad:
        parentName === "signIn" ? yup.boolean() : yup.boolean().nullable(),

      VerifiadGetSms:
        parentName === "signIn" ? yup.boolean() : yup.boolean().nullable(),
    })
    .required();

  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const preload = useSelector((state) => state.openCart.preload);
  const user = useSelector((state) => state.user.user);
  const currentStationId = useSelector((state) => state.user.stationId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues:
      parentName !== "signIn" && !isGuest && user
        ? {
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            phone: user.phone || "",
          }
        : {},
  });

  useEffect(() => {
    if (parentName !== "signIn" && !isGuest && user) {
      reset({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
      });
    }
  }, [user, parentName, isGuest, reset]);

  const send = async (data) => {
    if (!preload) {
      dispatch(setPreload(true));
      onClearInlineError?.();

      if (parentName === "signIn") {
        let savedAddress = null;
        try {
          savedAddress = JSON.parse(localStorage.getItem("tempStation"));
        } catch {
          savedAddress = null;
        }

        const stationId = currentStationId;

        const payload = {
          ...data,
          tenant: "hamazon",
          username: data.email.toLowerCase(),
          defaultLocation: stationId,
          StationId: stationId,
          addrCity: savedAddress?.city || "",
          addrStreet: savedAddress?.streetName || "",
          addrHouse: savedAddress?.houseNumber || "",
          addrFloor: savedAddress?.floor || "",
          addrApartment: savedAddress?.apartment || "",
          addrZipCode: savedAddress?.zipCode || "",
          Active: 1,
        };
        const orderRecipientName = String(savedAddress?.name ?? "").trim();
        if (orderRecipientName) {
          payload.addrOrderRecipientName = orderRecipientName;
        }

        if (!data.Verifiad) {
          onInlineError?.("יש לאשר את התקנון והצהרת הגיל", "נדרש אישור");
          dispatch(setPreload(false));
          return;
        }

        try {
          await dispatch(customerRegister(payload, { suppressAlert: false }));
          onRegistered?.();
        } catch (e) {
          // Only show inline error if it's not an email conflict (409)
          // Email conflicts show SweetAlert popup instead
          if (!e.message.includes("כבר קיימת")) {
            onInlineError?.(
              e?.message || "שגיאה ברישום",
              "לא הצלחנו להשלים הרשמה",
            );
          }
        } finally {
          dispatch(setPreload(false));
        }
      } else {
        const cleanPayload = buildProfileUpdatePayload({
          ...user,
          ...data,
          defaultLocation: currentStationId,
          stationId: currentStationId,
        });

        UpdateUser(cleanPayload)
          .then(() => {
            dispatch({ type: "UPDATE_USER", payload: cleanPayload });
            showAlert({ msgTitle: "הפרטים נשמרו בהצלחה", msgType: "success" });
          })
          .catch((err) => {
            const serverMsg =
              err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.response?.data?.Message ||
              err?.response?.data?.Massage;
            onInlineError?.(serverMsg || "שגיאה בעדכון הפרטים", "שגיאה");
          })
          .finally(() => dispatch(setPreload(false)));
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPassVisibility = () => {
    setShowConfirmPass(!showConfirmPass);
  };

  return (
    <Fragment>
      <form className="register-form" onSubmit={handleSubmit(send)}>
        {parentName === "signIn" && (
          <span className="note">שדות חובה מסומנים ב*</span>
        )}

        <div className="details-form">
          <div className="row-form">
            <Input
              type="text"
              register={register}
              label={parentName === "endShopping" ? "שם פרטי" : "שם פרטי*"}
              disabled={parentName === "endShopping"}
              errors={errors}
              name="firstName"
            />
            <Input
              type="text"
              register={register}
              label={parentName === "endShopping" ? "שם משפחה" : "שם משפחה*"}
              disabled={parentName === "endShopping"}
              errors={errors}
              name="lastName"
            />
          </div>

          <div className="row-form">
            <Input
              type="number"
              register={register}
              label={parentName === "signIn" ? "טלפון*" : "טלפון"}
              errors={errors}
              name="phone"
            />

            {parentName === "signIn" && (
              <Input
                type="number"
                register={register}
                label="טלפון נוסף"
                errors={errors}
                name="phone2"
              />
            )}
          </div>

          <div className="row-form">
            {parentName === "signIn" && (
              <div style={{ width: "100%" }}>
                <Input
                  type={showPassword ? "text" : "password"}
                  register={register}
                  errors={errors}
                  label="סיסמה*"
                  name="password"
                  toggleVisibility={togglePasswordVisibility}
                  showPassword={showPassword}
                  isPasswordField
                />
              </div>
            )}

            {parentName === "signIn" && (
              <Input
                type={showConfirmPass ? "text" : "password"}
                register={register}
                errors={errors}
                label="אימות סיסמה*"
                name="confirmPass"
                toggleVisibility={toggleShowConfirmPassVisibility}
                showPassword={showConfirmPass}
                isPasswordField
              />
            )}
          </div>
          {parentName === "signIn" && (
            <div className="row-form">
              <Input
                type="email"
                register={register}
                label="דואר אלקטרוני*"
                errors={errors}
                name="email"
              />
              <span></span>
            </div>
          )}
          {parentName === "signIn" && (
            <>
              <div className="row-form">
                <div className="checkbox-style">
                  <label className="checkbox-consent">
                    <input type="checkbox" {...register("Verifiad")} />
                    <span className="verify">
                      הנני מאשר/ת כי קראתי את{" "}
                      <a
                        target="_blank"
                        href={globalFileServer + "policy_form.pdf"}
                        rel="noreferrer"
                      >
                        תקנון
                      </a>{" "}
                      האתר, ומצהיר/ה כי אני מעל גיל 18
                    </span>
                  </label>
                </div>
              </div>
              <div className="row-form">
                <div className="checkbox-style">
                  <label className="checkbox-consent">
                    <input type="checkbox" {...register("VerifiadGetSms")} />
                    <span className="verify">
                      הנני מאשר/ת לקבל הטבות, ועדכונים בדוא״ל וב-SMS (ניתן
                      להפסיק את קבלת העדכונים בכל עת).
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}

          <div className="row-form">
            {parentName !== "endShopping" && (
              <button
                className="register-btn"
                type="submit"
                disabled={parentName !== "signIn" && !isGuest && !user}
              >
                {parentName === "signIn" ? (
                  <>
                    הרשמה
                    <ChevronLeft size={16} strokeWidth={2.4} aria-hidden />
                  </>
                ) : (
                  "עדכון"
                )}
              </button>
            )}

            {parentName === "signIn" && (
              <button
                type="button"
                className="cancel"
                onClick={() => dispatch(setUserEntry([false, "login"]))}
              >
                כניסה ללא הרשמה
                <ChevronLeft size={14} strokeWidth={2.4} aria-hidden />
              </button>
            )}
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default UserEntry;
